import React from 'react';
import Icon from '../AppIcon';

const FormProgressIndicator = ({ 
  isVisible = false, 
  progress = 0, 
  status = 'idle', // 'idle', 'uploading', 'processing', 'success', 'error'
  message = '',
  fileName = '',
  fileSize = '',
  onCancel = null,
  onRetry = null 
}) => {
  if (!isVisible) return null;

  const getStatusIcon = () => {
    switch (status) {
      case 'uploading':
        return <Icon name="Upload" size={20} className="animate-pulse" />;
      case 'processing':
        return <Icon name="Loader2" size={20} className="animate-spin" />;
      case 'success':
        return <Icon name="CheckCircle" size={20} color="#10B981" />;
      case 'error':
        return <Icon name="AlertCircle" size={20} color="#DC2626" />;
      default:
        return <Icon name="FileText" size={20} />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'uploading': case'processing':
        return 'text-primary';
      case 'success':
        return 'text-success';
      case 'error':
        return 'text-error';
      default:
        return 'text-text-secondary';
    }
  };

  const getProgressBarColor = () => {
    switch (status) {
      case 'success':
        return 'bg-success';
      case 'error':
        return 'bg-error';
      default:
        return 'bg-primary';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
      <div className="bg-surface rounded-xl shadow-elevation-4 p-6 w-full max-w-md animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`${getStatusColor()}`}>
              {getStatusIcon()}
            </div>
            <h3 className="text-lg font-semibold text-text-primary font-inter">
              {status === 'uploading' && 'Téléchargement en cours'}
              {status === 'processing' && 'Traitement du document'}
              {status === 'success' && 'Téléchargement réussi'}
              {status === 'error' && 'Erreur de téléchargement'}
              {status === 'idle' && 'Préparation'}
            </h3>
          </div>
          
          {onCancel && (status === 'uploading' || status === 'processing') && (
            <button
              onClick={onCancel}
              className="p-1 rounded-lg text-text-muted hover:text-text-primary hover:bg-secondary-50 transition-smooth focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label="Annuler"
            >
              <Icon name="X" size={20} />
            </button>
          )}
        </div>

        {/* File Information */}
        {fileName && (
          <div className="mb-4 p-3 bg-secondary-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Icon name="FileText" size={16} color="#64748B" />
              <span className="text-sm font-medium text-text-primary font-inter truncate">
                {fileName}
              </span>
            </div>
            {fileSize && (
              <span className="text-xs text-text-secondary font-inter">
                {fileSize}
              </span>
            )}
          </div>
        )}

        {/* Progress Bar */}
        {(status === 'uploading' || status === 'processing') && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-primary font-inter">
                Progression
              </span>
              <span className="text-sm font-medium text-primary font-inter">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="w-full bg-border rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ease-out ${getProgressBarColor()}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Status Message */}
        {message && (
          <div className="mb-4">
            <p className={`text-sm font-inter ${getStatusColor()}`}>
              {message}
            </p>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="mb-4 p-3 bg-success-50 rounded-lg border border-success-100">
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} color="#10B981" />
              <span className="text-sm font-medium text-success font-inter">
                Document téléchargé avec succès
              </span>
            </div>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="mb-4 p-3 bg-error-50 rounded-lg border border-error-100">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="AlertCircle" size={16} color="#DC2626" />
              <span className="text-sm font-medium text-error font-inter">
                Erreur lors du téléchargement
              </span>
            </div>
            {message && (
              <p className="text-sm text-error font-inter">
                {message}
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3">
          {status === 'error' && onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm font-inter hover:bg-primary-600 transition-smooth focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              Réessayer
            </button>
          )}
          
          {status === 'success' && (
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-success text-success-foreground rounded-lg font-medium text-sm font-inter hover:bg-accent-700 transition-smooth focus:outline-none focus:ring-2 focus:ring-success focus:ring-offset-2"
            >
              Continuer
            </button>
          )}

          {(status === 'uploading' || status === 'processing') && (
            <div className="flex items-center space-x-2 text-text-secondary">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-sm font-inter">
                {status === 'uploading' ? 'Téléchargement...' : 'Traitement...'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormProgressIndicator;